import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Users, Phone, Mail, MapPin, Calendar, CheckCircle2, Flag, ClipboardList } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatPhone, formatVisitDate } from "@/lib/utils";

function calcAge(birthMonth, birthYear) {
  if (!birthYear) return null;
  const today = new Date();
  let age = today.getFullYear() - birthYear;
  if (birthMonth && today.getMonth() + 1 < birthMonth) age -= 1;
  return Math.max(age, 0);
}

function fmt(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function isDiff(a, b) {
  const av = (a === null || a === undefined || a === "") ? "" : String(a);
  const bv = (b === null || b === undefined || b === "") ? "" : String(b);
  return av !== bv;
}

const DIFF_BG = { background: "#FEF3C7", borderColor: "#F59E0B" };
const SAME_BG = {};

function FieldRow({ label, valueA, valueB, formatFn = fmt }) {
  const diff = isDiff(valueA, valueB);
  return (
    <div className={`grid grid-cols-[140px_1fr_1fr] gap-0 border-b`} style={{ borderColor: "var(--fb-border)" }}>
      <div
        className="px-3 py-2 text-xs font-semibold flex items-center"
        style={{ color: "var(--fb-text-muted)", background: "var(--fb-bg)" }}
      >
        {label}
      </div>
      <div
        className="px-3 py-2 text-sm border-l"
        style={{ borderColor: "var(--fb-border)", ...(diff ? DIFF_BG : SAME_BG) }}
      >
        {formatFn(valueA)}
      </div>
      <div
        className="px-3 py-2 text-sm border-l"
        style={{ borderColor: "var(--fb-border)", ...(diff ? DIFF_BG : SAME_BG) }}
      >
        {formatFn(valueB)}
      </div>
    </div>
  );
}

function MemberList({ members }) {
  if (!members || members.length === 0) {
    return <span className="text-xs" style={{ color: "var(--fb-text-muted)" }}>None listed</span>;
  }
  return (
    <ul className="space-y-1">
      {members.map((m, i) => {
        const age = m.age ?? calcAge(m.birth_month, m.birth_year);
        return (
          <li key={m.id || i} className="text-xs flex items-center gap-1">
            <span className="font-medium">{m.first_name} {m.last_name}</span>
            {age != null && <span style={{ color: "var(--fb-text-muted)" }}>(age {age})</span>}
            {m.gender && <span style={{ color: "var(--fb-text-muted)" }}>· {m.gender}</span>}
            {m.relationship && <span style={{ color: "var(--fb-text-muted)" }}>· {m.relationship}</span>}
          </li>
        );
      })}
    </ul>
  );
}

function VisitList({ visits }) {
  if (!visits || visits.length === 0) {
    return <span className="text-xs" style={{ color: "var(--fb-text-muted)" }}>No recent visits</span>;
  }
  return (
    <ul className="space-y-0.5">
      {visits.map((v, i) => (
        <li key={v.id || i} className="text-xs" style={{ color: "var(--fb-text)" }}>
          {formatVisitDate(v.visit_date)}
          {v.notes ? <span style={{ color: "var(--fb-text-muted)" }}> — {v.notes}</span> : null}
        </li>
      ))}
    </ul>
  );
}

function FlagList({ flags }) {
  if (!flags || flags.length === 0) return <span className="text-xs" style={{ color: "var(--fb-text-muted)" }}>None</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {flags.map((f) => (
        <span key={f} className="text-[10px] px-2 py-0.5 rounded-full font-semibold fb-status-warning">{f.replace(/_/g, " ")}</span>
      ))}
    </div>
  );
}

function ConfirmModal({ open, title, description, confirmLabel, onConfirm, onCancel, busy }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      data-testid="compare-confirm-overlay"
    >
      <div
        className="fb-card p-6 w-full max-w-md mx-4 shadow-xl"
        role="dialog"
        aria-modal="true"
        data-testid="compare-confirm-modal"
      >
        <h2 className="text-base font-semibold mb-2" style={{ fontFamily: "Outfit" }}>{title}</h2>
        <p className="text-sm mb-5" style={{ color: "var(--fb-text)" }}>{description}</p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-sm font-semibold px-4 py-2 rounded-lg border hover:bg-stone-50 disabled:opacity-50"
            style={{ borderColor: "var(--fb-border)", color: "var(--fb-text)" }}
            data-testid="compare-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50"
            style={{ background: "var(--fb-error)" }}
            data-testid="compare-modal-confirm"
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CompareClients() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isViewer = user?.role === "viewer";

  const idA = searchParams.get("a");
  const idB = searchParams.get("b");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clientA, setClientA] = useState(null);
  const [clientB, setClientB] = useState(null);

  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  const pendingMerge = useRef(null);

  useEffect(() => {
    if (!idA || !idB) {
      setError("Two client IDs (a and b) are required.");
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get(`/clients/compare?a=${encodeURIComponent(idA)}&b=${encodeURIComponent(idB)}`)
      .then(({ data }) => {
        setClientA(data.client_a);
        setClientB(data.client_b);
      })
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [idA, idB]);

  function nameOf(c) {
    if (!c) return "";
    return `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Unknown";
  }

  function openKeepAModal() {
    const keepName = nameOf(clientA);
    const discardName = nameOf(clientB);
    setModal({
      title: `Keep ${keepName}`,
      description: `${discardName}'s visit history and records will be merged into ${keepName}'s profile. ${discardName} will be permanently deleted. You'll stay on this page.`,
      confirmLabel: `Yes, keep ${keepName}`,
    });
    pendingMerge.current = { keepId: idA, discardId: idB, navigateTo: `/staff/clients/${idA}` };
  }

  function openKeepBModal() {
    const keepName = nameOf(clientB);
    const discardName = nameOf(clientA);
    setModal({
      title: `Keep ${keepName}`,
      description: `${discardName}'s visit history and records will be merged into ${keepName}'s profile. ${discardName} will be permanently deleted. You'll be redirected to ${keepName}'s page.`,
      confirmLabel: `Yes, keep ${keepName}`,
    });
    pendingMerge.current = { keepId: idB, discardId: idA, navigateTo: `/staff/clients/${idB}` };
  }

  async function handleConfirm() {
    const { keepId, discardId, navigateTo } = pendingMerge.current;
    setBusy(true);
    setActionError("");
    setModal(null);
    try {
      await api.post("/clients/merge", { keep_id: keepId, discard_id: discardId });
      navigate(navigateTo, { replace: true });
    } catch (e) {
      setActionError(formatApiError(e));
    } finally {
      setBusy(false);
      pendingMerge.current = null;
    }
  }

  async function handleKeepBoth() {
    setBusy(true);
    setActionError("");
    try {
      await Promise.allSettled([
        api.delete(`/clients/${idA}/flags/possible_duplicate`),
        api.delete(`/clients/${idB}/flags/possible_duplicate`),
      ]);
      navigate("/staff/clients", { replace: true });
    } catch (e) {
      setActionError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  function handleCancel() {
    setModal(null);
    pendingMerge.current = null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-sm" style={{ color: "var(--fb-text-muted)" }}>
        Loading comparison…
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Link to="/staff/clients" className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline" style={{ color: "var(--fb-primary)" }}>
          <ArrowLeft className="w-4 h-4" /> Back to clients
        </Link>
        <div className="fb-card p-6 text-sm" style={{ color: "var(--fb-error)" }}>{error}</div>
      </div>
    );
  }

  const nameA = nameOf(clientA);
  const nameB = nameOf(clientB);

  const diffCount = [
    isDiff(clientA?.first_name + " " + clientA?.last_name, clientB?.first_name + " " + clientB?.last_name),
    isDiff(clientA?.address, clientB?.address),
    isDiff(clientA?.phone, clientB?.phone),
    isDiff(clientA?.email, clientB?.email),
    isDiff(clientA?.gender, clientB?.gender),
    isDiff(clientA?.eligible, clientB?.eligible),
  ].filter(Boolean).length;

  return (
    <>
      <ConfirmModal
        open={!!modal}
        title={modal?.title}
        description={modal?.description}
        confirmLabel={modal?.confirmLabel}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        busy={busy}
      />

      <div className="space-y-5 pb-20" data-testid="compare-page">
        <div className="flex items-center gap-3 flex-wrap">
          <Link
            to="/staff/clients"
            className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
            style={{ color: "var(--fb-primary)" }}
            data-testid="compare-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to clients
          </Link>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-1" style={{ color: "#8B918B" }}>
            Household Comparison
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>
            Compare Households
          </h1>
          {diffCount > 0 && (
            <div className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-xs font-semibold fb-status-warning">
              <AlertTriangle className="w-3.5 h-3.5" />
              {diffCount} field{diffCount !== 1 ? "s" : ""} differ
            </div>
          )}
        </div>

        {actionError && (
          <div className="fb-card p-4 text-sm fb-status-error">{actionError}</div>
        )}

        {!isViewer && (
          <div className="fb-card p-4">
            <div className="text-xs uppercase tracking-wider font-semibold mb-3" style={{ color: "#8B918B" }}>
              Actions
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                data-testid="keep-a-btn"
                onClick={openKeepAModal}
                disabled={busy}
                className="flex flex-col items-start gap-1 px-4 py-3 rounded-xl border-2 text-left transition-colors hover:border-[#052F66] disabled:opacity-50"
                style={{ borderColor: "var(--fb-border)" }}
              >
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--fb-primary)" }}>
                  Merge — keep left
                </span>
                <span className="text-sm font-semibold" style={{ color: "var(--fb-text)" }}>{nameA}</span>
                <span className="text-xs" style={{ color: "var(--fb-text-muted)" }}>
                  Right record will be deleted
                </span>
              </button>
              <button
                data-testid="keep-b-btn"
                onClick={openKeepBModal}
                disabled={busy}
                className="flex flex-col items-start gap-1 px-4 py-3 rounded-xl border-2 text-left transition-colors hover:border-[#052F66] disabled:opacity-50"
                style={{ borderColor: "var(--fb-border)" }}
              >
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--fb-primary)" }}>
                  Merge — keep right
                </span>
                <span className="text-sm font-semibold" style={{ color: "var(--fb-text)" }}>{nameB}</span>
                <span className="text-xs" style={{ color: "var(--fb-text-muted)" }}>
                  Left record will be deleted
                </span>
              </button>
              <button
                data-testid="keep-both-btn"
                onClick={handleKeepBoth}
                disabled={busy}
                className="flex flex-col items-start gap-1 px-4 py-3 rounded-xl border-2 text-left transition-colors hover:border-stone-400 disabled:opacity-50"
                style={{ borderColor: "var(--fb-border)" }}
              >
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--fb-text-muted)" }}>
                  Keep both
                </span>
                <span className="text-sm font-semibold" style={{ color: "var(--fb-text)" }}>Not a duplicate</span>
                <span className="text-xs" style={{ color: "var(--fb-text-muted)" }}>
                  Dismiss duplicate flags and return to client list
                </span>
              </button>
            </div>
          </div>
        )}

        <div className="fb-card overflow-hidden">
          <div className="grid grid-cols-[140px_1fr_1fr] border-b" style={{ borderColor: "var(--fb-border)", background: "var(--fb-bg)" }}>
            <div className="px-3 py-3" />
            <div className="px-3 py-3 border-l font-semibold text-sm" style={{ borderColor: "var(--fb-border)", color: "var(--fb-primary)" }}>
              <Link
                to={`/staff/clients/${idA}`}
                className="hover:underline"
                data-testid="compare-link-a"
              >
                {nameA}
              </Link>
              <div className="text-xs font-normal mt-0.5" style={{ color: "var(--fb-text-muted)" }}>
                {clientA?.household_size} person household · {clientA?.visit_count_12mo ?? 0} visit{clientA?.visit_count_12mo !== 1 ? "s" : ""} (12mo)
              </div>
            </div>
            <div className="px-3 py-3 border-l font-semibold text-sm" style={{ borderColor: "var(--fb-border)", color: "var(--fb-primary)" }}>
              <Link
                to={`/staff/clients/${idB}`}
                className="hover:underline"
                data-testid="compare-link-b"
              >
                {nameB}
              </Link>
              <div className="text-xs font-normal mt-0.5" style={{ color: "var(--fb-text-muted)" }}>
                {clientB?.household_size} person household · {clientB?.visit_count_12mo ?? 0} visit{clientB?.visit_count_12mo !== 1 ? "s" : ""} (12mo)
              </div>
            </div>
          </div>

          <FieldRow
            label="Name"
            valueA={`${clientA?.first_name || ""} ${clientA?.last_name || ""}`.trim()}
            valueB={`${clientB?.first_name || ""} ${clientB?.last_name || ""}`.trim()}
          />
          <FieldRow
            label="Phone"
            valueA={clientA?.phone ? formatPhone(clientA.phone) : ""}
            valueB={clientB?.phone ? formatPhone(clientB.phone) : ""}
          />
          <FieldRow label="Email" valueA={clientA?.email} valueB={clientB?.email} />
          <FieldRow label="Address" valueA={clientA?.address} valueB={clientB?.address} />
          <FieldRow label="Gender" valueA={clientA?.gender} valueB={clientB?.gender} />
          <FieldRow
            label="Birth year"
            valueA={clientA?.birth_year}
            valueB={clientB?.birth_year}
          />
          <FieldRow
            label="Addr. verified"
            valueA={clientA?.address_verified}
            valueB={clientB?.address_verified}
            formatFn={(v) => (v ? "Yes" : "No")}
          />
          <FieldRow
            label="Eligible"
            valueA={clientA?.eligible}
            valueB={clientB?.eligible}
            formatFn={(v) => (v === false ? "No" : "Yes")}
          />
          <FieldRow label="Notes" valueA={clientA?.notes} valueB={clientB?.notes} />
          <FieldRow
            label="Last visit"
            valueA={clientA?.last_visit_date ? formatVisitDate(clientA.last_visit_date) : ""}
            valueB={clientB?.last_visit_date ? formatVisitDate(clientB.last_visit_date) : ""}
          />

          <div className="grid grid-cols-[140px_1fr_1fr] border-b" style={{ borderColor: "var(--fb-border)" }}>
            <div
              className="px-3 py-3 text-xs font-semibold flex items-start pt-2.5"
              style={{ color: "var(--fb-text-muted)", background: "var(--fb-bg)" }}
            >
              <div className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" /> Members
              </div>
            </div>
            <div className="px-3 py-3 border-l text-sm" style={{ borderColor: "var(--fb-border)" }}>
              <MemberList members={clientA?.household_members} />
            </div>
            <div className="px-3 py-3 border-l text-sm" style={{ borderColor: "var(--fb-border)" }}>
              <MemberList members={clientB?.household_members} />
            </div>
          </div>

          <div className="grid grid-cols-[140px_1fr_1fr] border-b" style={{ borderColor: "var(--fb-border)" }}>
            <div
              className="px-3 py-3 text-xs font-semibold flex items-start pt-2.5"
              style={{ color: "var(--fb-text-muted)", background: "var(--fb-bg)" }}
            >
              <div className="flex items-center gap-1">
                <ClipboardList className="w-3.5 h-3.5" /> Recent visits
              </div>
            </div>
            <div className="px-3 py-3 border-l text-sm" style={{ borderColor: "var(--fb-border)" }}>
              <VisitList visits={clientA?.recent_visits} />
            </div>
            <div className="px-3 py-3 border-l text-sm" style={{ borderColor: "var(--fb-border)" }}>
              <VisitList visits={clientB?.recent_visits} />
            </div>
          </div>

          <div className="grid grid-cols-[140px_1fr_1fr]" style={{}}>
            <div
              className="px-3 py-3 text-xs font-semibold flex items-start pt-2.5"
              style={{ color: "var(--fb-text-muted)", background: "var(--fb-bg)" }}
            >
              <div className="flex items-center gap-1">
                <Flag className="w-3.5 h-3.5" /> Flags
              </div>
            </div>
            <div className="px-3 py-3 border-l" style={{ borderColor: "var(--fb-border)" }}>
              <FlagList flags={clientA?.import_flags} />
            </div>
            <div className="px-3 py-3 border-l" style={{ borderColor: "var(--fb-border)" }}>
              <FlagList flags={clientB?.import_flags} />
            </div>
          </div>
        </div>

        {!isViewer && (
          <div className="flex justify-center gap-3 flex-wrap pt-2">
            <button
              data-testid="keep-a-btn-bottom"
              onClick={openKeepAModal}
              disabled={busy}
              className="fb-btn-primary px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50"
            >
              Merge — keep {nameA}
            </button>
            <button
              data-testid="keep-b-btn-bottom"
              onClick={openKeepBModal}
              disabled={busy}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold border disabled:opacity-50 hover:bg-stone-50"
              style={{ borderColor: "var(--fb-border)", color: "var(--fb-text)" }}
            >
              Merge — keep {nameB}
            </button>
            <button
              data-testid="keep-both-btn-bottom"
              onClick={handleKeepBoth}
              disabled={busy}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold border hover:bg-stone-50 disabled:opacity-50"
              style={{ borderColor: "var(--fb-border)", color: "var(--fb-text-muted)" }}
            >
              Keep both (not a duplicate)
            </button>
          </div>
        )}
      </div>
    </>
  );
}
